import { isString, SDate, SDuration } from '@summit/core';
import {
  DurationFormatter,
  MileageFormatter,
  MoneyFormatter,
  NumberFormatter,
  PercentageFormatter,
  RateFormatter,
  SummitFormattedText
} from '@summit/models';

import { WorkersCompCode } from '@app/models/reports2/insurance/workers-comp-code.enum';
import { Duration } from 'dayjs/plugin/duration';
import { EboObjectTimeLine } from '../../base-classes/ebo-object-time-line';
import { PayScheduleMapping } from '../../payroll/shared/pay-schedule.enum';
import { EmployeePayrollGroup } from '../employee-payroll-group';
import { EmployeeWageExtra } from './employee-wage-extra';
import { EmployeeWageIncentive } from './employee-wage-incentive';
import { EmployeeWageRule } from './employee-wage-rule';
import { WageConditionalAction } from './wage-conditional-action.enum';
import { WageConditionalMethod, WageConditionalMethodMapping } from './wage-conditional-method.enum';
import { WageConditionalOperator, WageConditionalOperatorMapping } from './wage-conditional-operator.enum';
import { WageExtraTypeMapping } from './wage-extra-type.enum';
import { WageType, WageTypeMapping } from './wage-type.enum';

export class EmployeeWage extends EboObjectTimeLine {
  // Enum Maps
  //*noUnusedParameters private static conditionalActionMap = WageConditionalActionMapping;
  private static conditionalMethodMap = WageConditionalMethodMapping;
  private static conditionalOperatorMap = WageConditionalOperatorMapping;
  public static EarnedWageTypes = [
    WageType.Earned,
    WageType.EarnedWithDayThreshold,
    WageType.EarnedAndDay,
    WageType.EarnedAndWeek,
    WageType.EarnedAndDayAndWeek,
    WageType.EarnedOrDay,
    WageType.EarnedOrDayAvg,
    WageType.EarnedOrWeek,
    WageType.HrSpSoDayPlusTeamMiles,
    WageType.EarnedOrDayPlusTeamMiles,
    WageType.SpotDayPlusLh,
    WageType.HrOrDaysPlusTeamMiles
  ];
  public static HideSoloRates = [WageType.HrSpSoDayPlusTeamMiles, WageType.HrOrDaysPlusTeamMiles];
  public static EarnedHrTypes = [
    WageType.Earned,
    WageType.EarnedWithDayThreshold,
    WageType.EarnedAndDay,
    WageType.EarnedAndWeek,
    WageType.EarnedAndDayAndWeek,
    WageType.EarnedOrDay,
    WageType.EarnedOrDayAvg,
    WageType.EarnedOrWeek,
    WageType.EarnedOrDayPlusTeamMiles,
    WageType.SpotDayPlusLh,
    WageType.HoursOnly,
    WageType.HoursWithThresholds,
    WageType.HrOrDaysPlusTeamMiles,
    WageType.HrPlusTeamDays
  ];
  public static DayWageTypes = [
    WageType.DayOnly,
    WageType.DayWithThreshold,
    WageType.DayAndWeek,
    WageType.EarnedAndDay,
    WageType.EarnedAndDayAndWeek,
    WageType.EarnedOrDay,
    WageType.EarnedOrDayAvg,
    WageType.EarnedNoMileageAndDay,
    WageType.EarnedOrDayPlusTeamMiles,
    WageType.LhDayPlusSpots,
    WageType.HrSpSoDayPlusTeamMiles,
    WageType.SpotDayPlusLh,
    WageType.EarnedNoMileageOrDay,
    WageType.HrOrDaysPlusTeamMiles,
    WageType.HrPlusTeamDays
  ];
  public static WeekWageTypes = [
    WageType.WeekOnly,
    WageType.DayAndWeek,
    WageType.EarnedAndWeek,
    WageType.EarnedAndDayAndWeek,
    WageType.EarnedOrWeek,
    WageType.EarnedNoMileageAndWeek,
    WageType.NoMileageAndWeek
  ];
  public static ThresholdWageTypes = [
    WageType.DayWithThreshold,
    WageType.EarnedWithDayThreshold,
    WageType.HoursWithThresholds
  ];

  employeeID: string;
  payrollGroupID: string;
  payrollGroup: EmployeePayrollGroup;
  type: WageType;
  timeClockEnabled: boolean;
  //Rates
  hourlyRate: number;
  includeEldHours: boolean;
  soloRate: number;
  teamRate: number;
  perDiemEligible: boolean | null;
  perDiemTeamOnly: boolean | null;
  perDiemSoloRate: number;
  perDiemTeamRate: number;
  perDiemMax?: number | null;
  dayRate: number | null;
  dayCalcExcludesHours: boolean;
  dayRateNonWork: number;
  dayGuarantee: number | null;
  dayCountMax?: number;
  weekRate: number | null;
  weekDayCount: number | null;
  weekIncludedSoloMiles: number | null;
  weekIncludedTeamMiles: number | null;
  eldDeduction: Duration;
  eldAddition: Duration;
  //Overages
  mileageThreshold: number;
  mileageThresholdRate: number;
  mileageThresholdPayMin: number;
  stopThreshold: number;
  stopThresholdAmount: number;
  spotThreshold: number;
  spotThresholdAmount: number;
  addSpotPay: boolean;

  perLeg: number | null;
  perLegGrossPercentage: number | null;
  dhSplitWithTeammate: boolean;
  perLegWithDH: number | null;
  dhPercentage: number;
  perDhNone: number | null;
  perDhSingle: number | null;
  perDhDouble: number | null;
  perDhTriple: number | null;
  //Overrides
  overrideFlatRateCheck: boolean;
  minimumWageOverride: number | null;
  blockBringingHome: boolean;
  weekIncludesFullSettlementMiles: boolean;
  workersCompLhOverrideCode: WorkersCompCode;
  minimumDetentionTimeOverride: Duration;
  //Thresholds
  conditionalOperator: WageConditionalOperator;
  thresholdValue: number | null;
  thresholdMinDayCount: number | null;
  conditionalAction: WageConditionalAction;
  conditionalMethod: WageConditionalMethod;
  thresholdActionValue: number | null;
  extras: EmployeeWageExtra[];
  incentives: EmployeeWageIncentive[];
  rules: EmployeeWageRule[];

  constructor(jsonData: Partial<EmployeeWage>) {
    super(jsonData);
    if (jsonData) {
      if (jsonData.payrollGroup) this.payrollGroup = new EmployeePayrollGroup(jsonData.payrollGroup);
      if (jsonData.incentives)
        this.incentives = jsonData.incentives
          .map((data) => new EmployeeWageIncentive(data))
          .sort((a, b) => (a.incentive?.name > b.incentive?.name ? 1 : -1));
      if (jsonData.rules)
        this.rules = jsonData.rules
          .map((data) => new EmployeeWageRule(data))
          .sort((a, b) => (a.ruleOrder > b.ruleOrder ? 1 : -1));
      if (jsonData.extras) this.extras = jsonData.extras.map((data) => new EmployeeWageExtra(data));
      if (jsonData.eldDeduction) this.eldDeduction = SDuration.new(jsonData.eldDeduction);
      if (jsonData.eldAddition) this.eldAddition = SDuration.new(jsonData.eldAddition);
      if (jsonData.minimumDetentionTimeOverride)
        this.minimumDetentionTimeOverride = SDuration.new(jsonData.minimumDetentionTimeOverride);
    }
    this.effectiveDate ??= SDate.getNormalized();
    this.type ??= WageType.Earned;
    this.overrideFlatRateCheck ??= false;
    if (isString(this.type)) {
      this.type = parseInt(this.type);
    }
    this.soloRate ??= 0;
    this.teamRate ??= 0;
    this.hourlyRate ??= 0;
    this.incentives ??= [];
    this.extras ??= [];
    this.rules ??= [];
    this.dhSplitWithTeammate ??= false;
    this.addSpotPay ??= false;
    this.blockBringingHome ??= false;
    this.dayCalcExcludesHours ??= false;
    this.timeClockEnabled ??= false;
    this.includeEldHours ??= false;
    this.perDiemTeamOnly ??= false;
  }

  get conditional(): SummitFormattedText {
    return this.toWageRuleSummary();
  }

  get typeSummary(): SummitFormattedText {
    const response = new SummitFormattedText();
    response.AddLine(WageTypeMapping[this.type], ['flex', 'flex-row', 'justify-center', 'font-bold']);
    if (EmployeeWage.EarnedHrTypes.includes(this.type)) {
      if (this.timeClockEnabled) response.AddLine('Time Clock Enabled');
      if (this.includeEldHours) response.AddLine('Include ELD Hours');
    }
    return response;
  }
  set typeSummary(value: SummitFormattedText) {
    //NOP
  }

  get specialRates(): SummitFormattedText {
    const response = new SummitFormattedText();
    if (this.includeEldHours && this.eldDeduction)
      response.AddKeyValues('ELD Deduction', DurationFormatter(this.eldDeduction));
    if (this.includeEldHours && this.eldAddition)
      response.AddKeyValues('ELD Addition', DurationFormatter(this.eldAddition));
    if (this.includeEldHours && this.minimumDetentionTimeOverride)
      response.AddKeyValues('Min Detention Time', DurationFormatter(this.minimumDetentionTimeOverride));
    if (EmployeeWage.DayWageTypes.indexOf(this.type) >= 0) response.AddLine(`${MoneyFormatter(this.dayRate)} / Day`);
    if (EmployeeWage.DayWageTypes.indexOf(this.type) >= 0 && this.dayCalcExcludesHours)
      response.AddKeyValues(`Day`, `Hours Excluded`);
    if (EmployeeWage.DayWageTypes.indexOf(this.type) >= 0 && this.dayGuarantee) {
      response.AddLine(`${NumberFormatter(this.dayGuarantee)} Day Guarantee`);
      if (this.dayRateNonWork) response.AddLine(`${MoneyFormatter(this.dayRateNonWork)} Non-Work Day Rate`);
    }
    if (EmployeeWage.DayWageTypes.indexOf(this.type) >= 0 && this.dayCountMax)
      response.AddLine(`Max of ${NumberFormatter(this.dayCountMax)} Days`);
    if (EmployeeWage.WeekWageTypes.indexOf(this.type) >= 0) {
      response.AddLine(`${MoneyFormatter(this.weekRate)} / Week`);
      if (this.weekIncludedSoloMiles)
        response.AddLine(`${MileageFormatter(this.weekIncludedSoloMiles)} Included Solo Miles`);
      if (this.weekIncludesFullSettlementMiles) response.AddLine(`Week includes full Settlement Miles`);
    }
    if (EmployeeWage.ThresholdWageTypes.includes(this.type)) {
      if (this.mileageThreshold) {
        let lclDescription = `${RateFormatter(this.mileageThresholdRate)} over ${MileageFormatter(
          this.mileageThreshold
        )} Miles`;
        if (this.mileageThresholdPayMin) lclDescription += ` (${MoneyFormatter(this.mileageThresholdPayMin)} Min)`;
        response.AddLine(lclDescription);
      }
      if (this.stopThreshold)
        response.AddLine(
          `${MoneyFormatter(this.stopThresholdAmount)} over ${NumberFormatter(this.stopThreshold)} Stops`
        );
      if (this.spotThresholdAmount)
        response.AddLine(
          `${MoneyFormatter(this.spotThresholdAmount)} over ${NumberFormatter(this.spotThreshold)} Spots`
        );
    }
    if (this.perLeg) response.AddLine(`${MoneyFormatter(this.perLeg)} / Leg`);
    if (this.dhSplitWithTeammate) response.AddLine(`DH Pay Split with Teammate`);
    if (this.perLegWithDH) response.AddLine(`${MoneyFormatter(this.perLegWithDH)} / Leg with DH`);
    if (this.perLegGrossPercentage)
      response.AddLine(`${PercentageFormatter(this.perLegGrossPercentage / 100)} of Gross`);
    if (this.dhPercentage) response.AddLine(`${PercentageFormatter(this.dhPercentage / 100)} of DH`);
    if (this.perDhNone) response.AddLine(`${MoneyFormatter(this.perDhNone)} / Bobtail`);
    if (this.perDhSingle) response.AddLine(`${MoneyFormatter(this.perDhSingle)} / Single Trailer`);
    if (this.perDhDouble) response.AddLine(`${MoneyFormatter(this.perDhDouble)} / Double Trailer`);
    if (this.perDhTriple) response.AddLine(`${MoneyFormatter(this.perDhTriple)} / Triple Trailer`);
    if (this.perDiemEligible) {
      if (this.perDiemTeamOnly) response.AddLine(`+ Per Diem (Team Only)`);
      else response.AddLine(`+ Per Diem`);

      if (this.perDiemMax) response.AddLine(`+ Per Diem (Max: ${this.perDiemMax})`);
      else if (this.perDiemSoloRate || this.perDiemTeamRate)
        response.AddLine(
          `+ Per Diem (Rate: ${RateFormatter(this.perDiemSoloRate)}/${RateFormatter(this.perDiemTeamRate)})`
        );
    }
    if (this.overrideFlatRateCheck) response.AddLine(`+ No requirement for minimum miles`);
    if (this.type == WageType.DayWithThreshold && this.addSpotPay) response.AddLine(`+ Spot Wages`);
    if (this.minimumWageOverride)
      response.AddKeyValues(`Minimum Wage Override`, MoneyFormatter(this.minimumWageOverride));
    return response;
  }

  get allExtras(): SummitFormattedText {
    const response = new SummitFormattedText();
    this.incentives?.forEach((wageIncentive) => response.AddLine(wageIncentive.incentive.name, ['font-bold']));
    this.extras?.forEach((extra) => {
      if (extra.company) response.AddLine(`[${extra.company.name}]`);
      response.AddKeyValues(WageExtraTypeMapping[extra.type], PayScheduleMapping[extra.schedule]);
      if (extra.requireApprovedPay)
        response.AddLine(`${MoneyFormatter(extra.amount)} if worked`, ['flex', 'justify-end']);
      else response.AddLine(MoneyFormatter(extra.amount), ['flex', 'justify-end']);
      response.AddLine(extra.comment, ['flex', 'justify-end']);
    });

    return response;
  }

  get allRules(): SummitFormattedText {
    const response = new SummitFormattedText();
    this.rules?.forEach((rule) => {
      response.AddKeyValues(`Rule #${rule.ruleOrder}`, rule.dayString.toString());
      response.AddText(rule.paySummary);
    });
    return response;
  }

  toWageRuleSummary(): SummitFormattedText {
    const response = new SummitFormattedText();
    switch (this.conditionalOperator) {
      case WageConditionalOperator.Each:
        response.AddLine(
          `${MoneyFormatter(this.thresholdActionValue)} / ${
            EmployeeWage.conditionalMethodMap[this.conditionalMethod]
          } to ${this.thresholdValue}`
        );
        break;
      case WageConditionalOperator.Always:
        switch (this.conditionalAction) {
          case WageConditionalAction.Threshold:
            response.AddLine(`${MoneyFormatter(this.thresholdValue)}`);
            break;
          case WageConditionalAction.Action:
            response.AddLine(`${MoneyFormatter(this.thresholdActionValue)}`);
            break;
          case WageConditionalAction.None:
            break;
          default:
            break;
        }
        break;
      case null:
      case undefined:
      case WageConditionalOperator.None:
        break;
      default: {
        response.AddKeyValues(
          `If ${EmployeeWage.conditionalMethodMap[this.conditionalMethod]} ${
            EmployeeWage.conditionalOperatorMap[this.conditionalOperator]
          }`,
          this.conditionalMethod == WageConditionalMethod.Wage
            ? `${MoneyFormatter(this.thresholdValue)}`
            : this.thresholdValue
        );
        const rightAlign = ['flex', 'flex-row', 'justify-end'];
        if (this.thresholdMinDayCount)
          response.AddLine(`and worked ${NumberFormatter(this.thresholdMinDayCount)} days`, rightAlign);
        switch (this.conditionalAction) {
          case WageConditionalAction.Threshold:
            response.AddLine(`Pay ${MoneyFormatter(this.thresholdValue)}`, rightAlign);
            break;
          case WageConditionalAction.Action:
            response.AddLine(`Pay ${MoneyFormatter(this.thresholdActionValue)}`, rightAlign);
            break;
          case WageConditionalAction.None:
            break;
          default:
            break;
        }
        break;
      }
    }
    return response;
  }

  clearConditional() {
    this.conditionalAction = WageConditionalAction.None;
    this.conditionalMethod = WageConditionalMethod.None;
    this.thresholdActionValue = null;
    this.thresholdValue = null;
    this.thresholdMinDayCount = null;
  }

  toShortString(_format?: string): string {
    return `${this.effectiveDate.format('M/D/YY')} - ${RateFormatter(this.soloRate)}/${RateFormatter(this.teamRate)}`;
  }
}
